import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, CheckCircle2, Shield, Calendar, Wallet, Package, ListChecks, FileText, Building2 } from "lucide-react";

import { getClientContractByToken, acceptClientContract } from "@/lib/client-contracts.functions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/contracts/accept/$token")({
  component: AcceptPage,
  head: () => ({ meta: [{ title: "Review & Accept Agreement — what Finland" }] }),
});

function fmtEur(n: number) {
  return new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(n);
}

function parseMonths(dealLength: string): number | null {
  const m = dealLength.match(/(\d+)\s*(month|mo|m\b|year|yr|y\b)/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  if (unit.startsWith("y")) return n * 12;
  return n;
}

function AcceptPage() {
  const { token } = useParams({ from: "/contracts/accept/$token" });
  const getFn = useServerFn(getClientContractByToken);
  const acceptFn = useServerFn(acceptClientContract);
  const q = useQuery({ queryKey: ["public-contract", token], queryFn: () => getFn({ data: { token } }) });

  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ contractId: string; acceptedAt: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalValue = useMemo(() => {
    if (!q.data || q.data.status === "invalid" || q.data.status === "expired" || q.data.status === "cancelled") return null;
    const c = q.data.contract;
    if (!c) return null;
    const months = parseMonths(c.dealLength);
    return months ? months * c.monthlyPrice : null;
  }, [q.data]);

  if (q.isLoading) {
    return <Centered><Loader2 className="h-5 w-5 animate-spin" /></Centered>;
  }
  if (q.isError || !q.data) {
    return <Centered><p className="text-destructive">Failed to load agreement.</p></Centered>;
  }

  if (q.data.status === "invalid") return <Centered><Message title="Invalid Link" body="This agreement link is not valid." /></Centered>;
  if (q.data.status === "expired") return <Centered><Message title="Expired" body="This agreement link has expired. Please contact us for a new one." /></Centered>;
  if (q.data.status === "cancelled") return <Centered><Message title="Cancelled" body="This agreement has been cancelled." /></Centered>;

  if (done || q.data.status === "accepted") {
    const c = "contract" in q.data ? q.data.contract : null;
    const ts = done?.acceptedAt;
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border rounded-2xl p-8 text-center shadow-sm">
          <div className="h-14 w-14 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold mb-2">Agreement Confirmed</h1>
          <p className="text-muted-foreground text-sm mb-5">
            {done ? "Thank you. A confirmation email is on its way to your inbox." : "This agreement was already accepted."}
          </p>
          <div className="border-t pt-4 space-y-1.5 text-xs">
            {(done?.contractId || c?.id) && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Contract ID</span>
                <span className="font-mono text-foreground">{done?.contractId ?? c?.id}</span>
              </div>
            )}
            {ts && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Accepted</span>
                <span className="text-foreground">{new Date(ts).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const c = q.data.contract!;

  async function submit() {
    setSubmitting(true); setError(null);
    try {
      const res = await acceptFn({ data: { token, confirmed: true } });
      setDone({ contractId: res.contractId, acceptedAt: res.acceptedAt });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 via-background to-background">
      {/* Top bar */}
      <header className="border-b bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-4 w-4 text-primary" />
            <span>what Finland</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Secure agreement</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-32">
        {/* Title */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/60 border rounded-full px-2.5 py-1 mb-3">
            <FileText className="h-3 w-3" />
            Service Agreement
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2">Your Agreement With what Finland</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Please review the details below and confirm acceptance to activate your plan.</p>
        </div>

        {/* Summary card */}
        <div className="border rounded-2xl bg-card shadow-sm overflow-hidden">
          {/* Header strip */}
          <div className="px-5 sm:px-7 py-5 border-b bg-muted/30">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Client</div>
                <div className="text-lg sm:text-xl font-semibold">{c.clientName}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Monthly</div>
                <div className="text-2xl sm:text-3xl font-semibold tabular-nums">{fmtEur(c.monthlyPrice)}</div>
                <div className="text-xs text-muted-foreground">per month</div>
              </div>
            </div>
          </div>

          {/* Stat grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x">
            <Stat icon={<Calendar className="h-4 w-4" />} label="Deal Length" value={c.dealLength} />
            <Stat icon={<Wallet className="h-4 w-4" />} label="Monthly Price" value={fmtEur(c.monthlyPrice)} />
            <Stat
              icon={<Package className="h-4 w-4" />}
              label={totalValue ? "Total Value" : "Package"}
              value={totalValue ? fmtEur(totalValue) : (c.packageType || "—")}
            />
          </div>

          {/* Details */}
          <div className="px-5 sm:px-7 py-6 space-y-6 border-t">
            {(() => {
              const vatRate = Number((c as any).vatRate ?? 25.5);
              const months = parseMonths(c.dealLength);
              const items = c.includedServices ?? [];
              const monthlyNet = items.length
                ? items.reduce((a, s) => a + (Number(s.price) || 0), 0)
                : Number(c.monthlyPrice) || 0;
              const monthlyVat = monthlyNet * (vatRate / 100);
              const monthlyTotal = monthlyNet + monthlyVat;
              const contractTotal = months ? monthlyTotal * months : null;

              return (
                <>
                  <Section label="Agreement Summary">
                    <div className="rounded-xl border divide-y">
                      <SummaryRow label="Package" value={c.packageType || "—"} />
                      <SummaryRow label="Deal Length" value={c.dealLength} />
                      <SummaryRow label="VAT Rate" value={`${vatRate}%`} />
                    </div>
                  </Section>

                  {c.packageDetails && (
                    <Section label="Package Details">
                      <p className="whitespace-pre-wrap text-foreground/90 leading-relaxed">{c.packageDetails}</p>
                    </Section>
                  )}

                  <Section label="Invoice Breakdown" icon={<ListChecks className="h-3.5 w-3.5" />}>
                    <div className="overflow-x-auto rounded-xl border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <th className="text-left px-3 py-2.5 font-medium">Service / Item</th>
                            <th className="text-center px-2 py-2.5 font-medium w-12">Qty</th>
                            <th className="text-right px-2 py-2.5 font-medium">Net Price<br/><span className="normal-case tracking-normal text-[10px]">(VAT 0%)</span></th>
                            <th className="text-right px-2 py-2.5 font-medium">Tax</th>
                            <th className="text-right px-3 py-2.5 font-medium">Total<br/><span className="normal-case tracking-normal text-[10px]">(Incl. Tax)</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr className="border-t">
                              <td className="px-3 py-2.5">{c.packageType || "Package Fee"}</td>
                              <td className="px-2 py-2.5 text-center tabular-nums">1</td>
                              <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(monthlyNet)}</td>
                              <td className="px-2 py-2.5 text-right tabular-nums">{vatRate}%</td>
                              <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmtEur(monthlyTotal)} / mo</td>
                            </tr>
                          ) : items.map((s, i) => {
                            const net = Number(s.price) || 0;
                            const total = net * (1 + vatRate / 100);
                            return (
                              <tr key={i} className="border-t">
                                <td className="px-3 py-2.5">{s.name}</td>
                                <td className="px-2 py-2.5 text-center tabular-nums">1</td>
                                <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(net)}</td>
                                <td className="px-2 py-2.5 text-right tabular-nums">{vatRate}%</td>
                                <td className="px-3 py-2.5 text-right tabular-nums font-medium">
                                  {net > 0 ? `${fmtEur(total)} / mo` : fmtEur(0)}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="border-t bg-muted/20">
                            <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Monthly Subtotal (Net Total)</td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtEur(monthlyNet)}</td>
                          </tr>
                          <tr className="bg-muted/20">
                            <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Monthly VAT ({vatRate}%)</td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtEur(monthlyVat)}</td>
                          </tr>
                          <tr className="border-t bg-foreground/[0.04]">
                            <td colSpan={4} className="px-3 py-3 text-right text-xs uppercase tracking-wider font-semibold text-primary">Total Monthly Charge</td>
                            <td className="px-3 py-3 text-right tabular-nums font-bold text-primary">{fmtEur(monthlyTotal)} / month</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Section>

                  <Section label="Contract Summary">
                    <div className="rounded-xl border bg-muted/20 px-4 py-4 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider font-semibold text-primary">Total Contract Value</span>
                      <span className="text-xl font-bold tabular-nums text-primary">
                        {contractTotal !== null ? fmtEur(contractTotal) : "—"}
                      </span>
                    </div>
                    {months && (
                      <p className="text-xs text-muted-foreground mt-2 text-right">
                        {fmtEur(monthlyTotal)} × {months} months
                      </p>
                    )}
                  </Section>

                  {c.additionalNotes && (
                    <Section label="Notes">
                      <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">{c.additionalNotes}</p>
                    </Section>
                  )}
                </>
              );
            })()}
          </div>
        </div>


        {/* Trust strip */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Encrypted in transit</div>
          <div className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Audit-logged</div>
          <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Copy emailed on confirmation</div>
        </div>
      </div>

      {/* Sticky acceptance bar */}
      <div className="fixed bottom-0 inset-x-0 border-t bg-card/95 backdrop-blur shadow-[0_-4px_20px_-8px_rgba(0,0,0,0.08)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-start gap-3 cursor-pointer flex-1 select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
            />
            <span className="text-sm leading-snug">
              I confirm I have reviewed and accept this agreement and the{" "}
              <TermsDialog /> on behalf of <span className="font-medium">{c.clientName}</span>.
            </span>
          </label>
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <Button size="lg" disabled={!checked || submitting} onClick={submit} className="min-w-[180px]">
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm Acceptance</>
              )}
            </Button>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="px-5 sm:px-7 py-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-1">
        {icon}
        {label}
      </div>
      <div className="text-base font-medium tabular-nums">{value}</div>
    </div>
  );
}

function Section({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">
        {icon}
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center p-6">{children}</div>;
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div className="max-w-md text-center">
      <h1 className="text-2xl font-semibold mb-2">{title}</h1>
      <p className="text-muted-foreground text-sm">{body}</p>
    </div>
  );
}

function TermsDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="text-primary underline underline-offset-2 hover:opacity-80 font-medium">
          Terms and Conditions
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Terms and Conditions / Yleiset sopimusehdot</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="en" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="self-start">
            <TabsTrigger value="en">English</TabsTrigger>
            <TabsTrigger value="fi">Suomi</TabsTrigger>
          </TabsList>
          <TabsContent value="en" className="overflow-y-auto pr-2 mt-3">
            <TermsContent
              sections={[
                {
                  title: "1. Scope of Service",
                  items: [
                    "The service includes the services and content defined in the agreement summary (such as packages, feed posts, and stories).",
                    "Any additional work or extra publications beyond the agreed monthly quantity will be invoiced separately according to the current price list.",
                  ],
                },
                {
                  title: "2. Contract Term and Termination",
                  items: [
                    "The contract begins as a fixed-term agreement for the period defined in the summary (e.g., 6 months).",
                    "Automatic renewal: After the fixed term ends, the contract continues automatically as an open-ended agreement with a 1-month notice period, unless either party terminates it in writing before the end of the fixed term.",
                  ],
                },
                {
                  title: "3. Pricing and Payment Terms",
                  items: [
                    "Billing cycle: Invoices are sent on the 15th day of the current month.",
                    "Payment period: All invoices are due within 14 days of the invoice date.",
                    "Late payments: Statutory late payment interest and reminder fees will be charged on overdue payments. If a payment is more than 14 days overdue, the service provider has the right to suspend service delivery until all outstanding payments have been settled.",
                  ],
                },
                {
                  title: "4. Client Obligations and Materials",
                  items: [
                    "The client is responsible for providing all materials, logos, usage rights, and information required to perform the service on time.",
                    "If the service provider is unable to deliver the agreed publications due to a delay caused by the client, the monthly fee will still be invoiced in full.",
                  ],
                },
                {
                  title: "5. Copyright and Ownership",
                  items: [
                    "Copyright and ownership of the created marketing materials (images, videos, texts) transfer to the client only once the corresponding invoice has been paid in full.",
                  ],
                },
              ]}
            />
          </TabsContent>
          <TabsContent value="fi" className="overflow-y-auto pr-2 mt-3">
            <TermsContent
              sections={[
                {
                  title: "1. Palvelun laajuus",
                  items: [
                    "Palvelu sisältää sopimusyhteenvedossa määritellyt palvelut ja sisällöt (kuten paketit, feed-postaukset ja stoorit).",
                    "Kaikki sovitun kuukausimäärän ylittävät lisätyöt tai ylimääräiset julkaisut laskutetaan erikseen voimassa olevan hinnaston mukaan.",
                  ],
                },
                {
                  title: "2. Sopimuskausi ja irtisanominen",
                  items: [
                    "Sopimus alkaa määräaikaisena yhteenvedossa määritellyksi ajaksi (esim. 6 kuukautta).",
                    "Automaattinen jatkuminen: Määräaikaisen kauden päätyttyä sopimus jatkuu automaattisesti toistaiseksi voimassa olevana 1 kuukauden irtisanomisajalla, ellei kumpikaan osapuoli irtisano sitä kirjallisesti ennen määräaikaisuuden päättymistä.",
                  ],
                },
                {
                  title: "3. Hinnoittelu ja maksuehdot",
                  items: [
                    "Laskutusjakso: Lasku lähetetään kuluvan kuukauden 15. päivä.",
                    "Maksuaika: Kaikkien laskujen maksuaika on 14 päivää laskun päiväyksestä.",
                    "Maksuviivästykset: Viivästyneistä maksuista peritään lainmukainen viivästyskorko ja huomautuskulu. Jos maksu on viivästynyt yli 14 päivää, palveluntarjoajalla on oikeus keskeyttää palvelun tuottaminen, kunnes kaikki rastiin jääneet maksut on suoritettu.",
                  ],
                },
                {
                  title: "4. Asiakkaan velvollisuudet ja materiaalit",
                  items: [
                    "Asiakas vastaa siitä, että hän toimittaa kaikki palvelun suorittamiseen tarvittavat materiaalit, logot, käyttöoikeudet ja tiedot ajallaan.",
                    "Mikäli palveluntarjoaja ei pysty tekemään sovittuja julkaisuja asiakkaasta johtuvan viivästyksen vuoksi, kuukausihinta laskutetaan silti täysimääräisenä.",
                  ],
                },
                {
                  title: "5. Tekijänoikeudet ja omistusoikeus",
                  items: [
                    "Tekijänoikeudet ja omistusoikeus luotuihin markkinointimateriaaleihin (kuvat, videot, tekstit) siirtyvät asiakkaalle vasta silloin, kun kyseinen lasku on maksettu kokonaisuudessaan.",
                  ],
                },
              ]}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function TermsContent({ sections }: { sections: { title: string; items: string[] }[] }) {
  return (
    <div className="space-y-5 text-sm leading-relaxed">
      {sections.map((s) => (
        <section key={s.title}>
          <h3 className="font-semibold text-foreground mb-2">{s.title}</h3>
          <ul className="space-y-1.5 list-disc pl-5 text-muted-foreground">
            {s.items.map((it, i) => <li key={i}>{it}</li>)}
          </ul>
        </section>
      ))}
    </div>
  );
}
